import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { IReference } from '@shared/interfaces/reference.interface';
import { IReferenceRelationship } from '@shared/interfaces/reference-relationship.interface';
import moment from 'moment';
import { LocalStorage, SessionStorage } from 'ngx-webstorage';

export interface IReferenceEditRow {
  fieldName: string;
  label: string;
  original: string;
  current: string;
}

const FIELD_ORDER: string[] = [
  'first_name',
  'last_name',
  'relationship',
  'title',
  'company_name',
  'start_date',
  'end_date',
  'email',
  'phone'
];

const FIELD_LABELS: { [key: string]: string } = {
  first_name: 'First Name',
  last_name: 'Last Name',
  relationship: 'Relationship',
  title: 'Job Title',
  company_name: 'Company Name',
  start_date: 'Start Date',
  end_date: 'End Date',
  email: 'Email',
  phone: 'Phone'
};

const DATE_FIELDS: string[] = ['start_date', 'end_date'];
const EMPTY_PLACEHOLDER = '(empty)';

@Component({
  selector: 'app-sidebar-reference-question-set-edits-card',
  templateUrl: './sidebar.reference.question-set.edits-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SidebarReferenceQuestionSetEditsCardComponent {
  @Input() public reference: IReference;

  @SessionStorage() public referenceRelationships: IReferenceRelationship[];
  @LocalStorage() public lng: string;

  public get hasEdits(): boolean {
    return !!this.reference?.reference_edits?.has_edits;
  }

  public get rows(): IReferenceEditRow[] {
    const edits = this.reference?.reference_edits;
    if (!edits?.has_edits) {
      return [];
    }

    const editedFields = edits.edited_fields || [];

    return FIELD_ORDER
      .filter((f) => editedFields.includes(f))
      .map((fieldName) => {
        const fieldEdit = edits[fieldName] as { original: string; current: string } | undefined;
        const rawOriginal = fieldEdit?.original ?? '';
        const rawCurrent = fieldEdit?.current ?? '';
        return {
          fieldName,
          label: FIELD_LABELS[fieldName],
          original: this.formatValue(fieldName, rawOriginal, 'original'),
          current: this.formatValue(fieldName, rawCurrent, 'current')
        };
      });
  }

  private formatValue(fieldName: string, value: string, slot: 'original' | 'current'): string {
    if (!value) {
      if (fieldName === 'end_date' && slot === 'current') {
        return 'Present';
      }
      return EMPTY_PLACEHOLDER;
    }
    if (DATE_FIELDS.includes(fieldName)) {
      return moment(value).format('MMM YYYY');
    }
    if (fieldName === 'relationship') {
      return this.resolveRelationshipLabel(value);
    }
    return value;
  }

  private resolveRelationshipLabel(code: string): string {
    const lng = this.lng || 'en';
    const match = this.referenceRelationships?.find(
      (r: IReferenceRelationship) => r.value === code
    );
    if (!match) {
      return code;
    }
    const translated = match.relationship_languages?.find((l) => l.code === lng)?.label;
    return translated || match.label || code;
  }
}
